/* eslint-disable react/jsx-handler-names,complexity, react/require-default-props, max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { CircularProgress } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import SwitchVideoIcon from '@material-ui/icons/SwitchVideo';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeMute from '@material-ui/icons/VolumeMute';
import VolumeUp from '@material-ui/icons/VolumeUp';
import classNames from 'classnames';
import { isMobile } from 'react-device-detect';

import CookieStorageService from 'services/CookieStorageService';
import { translate } from 'services/i18nService';
import ChatStore from 'stores/ChatStore';
import GroupShowStore from 'stores/GroupShowStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import track from 'services/tracking';
import { BROADCAST_STATUS } from 'enum/videoPlayer';

import StreamQualityOptions from '../SmartXPlayer/Controls/StreamQualityOptions';
import Background from '../Background';

import JinkPlayerAdsContainer from './JinkPlayerAdsContainer';
import JinkPlayerTransformContainer from './JinkPlayerTransformContainer';
import styles from './JinkPlayer.css';
import DMCABadge from './DMCABadge';


const volumeHideDelay = 3000;
const hideUnmuteButtonDelay = 5000;

const JinkPlayerContainer = ({ adsIsPlaying, buffering, renderViewerControls, handleVideoDoubleClick, ...props }) => {
  const {
    broadcastStatus,
    deviceInLandscape,
    profileInfo,
    realityShow,
    setMainEventStream,
    mainEventSecondaryStream,
  } = ProfileStore;
  const { user: { username, sfw } } = UserStore;

  const {
    isDesktop,
    player,
    onHover,
    offHover,
    handleVolume,
    volume,
    isCustomFullscreenActive,
    closeCustomFullscreen,
    handleStreamQualityChange,
    temporaryPlayerService,
    videoLoaded,
  } = props;

  const {
    showQualityOptions,
    activeStreamQualityOption,
    streamQualityOptions,
    isInFullscreen,
  } = temporaryPlayerService;

  const [showPip, setShowPip] = useState(false);
  const [pipClosed, setPipClosed] = useState(false);
  const [isMuted, setIsMuted] = useState(player?.muted?.());
  const [hideVolume, setHideVolume] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [volumeButtons, setVolumeButtons] = useState(null);
  const [showUnmuteButton, setShowUnmuteButton] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowUnmuteButton(false), hideUnmuteButtonDelay);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    setVolumeButtons(!volumeButtons);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- componentDidMount
  }, []);

  const onUnmuteVideo = useCallback(() => {
    player.muted(false);
    setIsMuted(false);
    handleVolume();
  }, [player, handleVolume]);

  const showUnmute = (
    (player?.muted?.() && isMuted) &&
    isDesktop &&
    !buffering &&
    broadcastStatus !== BROADCAST_STATUS.SPYING
  );

  const container = useRef();
  const video = useRef();

  const onWindowBlur = useCallback(() => {
    if (isMobile) {
      player.pause();
    }
  }, [player]);

  const onWindowFocus = useCallback(() => {
    if (isMobile) {
      player.play();
    }
  }, [player]);

  useEffect(() => {
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);

    return () => {
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [onWindowBlur, onWindowFocus]);

  const onScroll = useCallback(() => {
    if (videoLoaded) {
      const bannerHeight = 76;
      const playerContainer = container.current.parentNode;
      const playerContainerHeight = playerContainer.getBoundingClientRect().height;
      setShowPip(window.pageYOffset >= playerContainerHeight + bannerHeight);
    }
  }, [videoLoaded]);

  const onClosePip = useCallback(() => {
    setPipClosed(true);
  }, []);

  const toggleVolumeButtons = useCallback(() => {
    setVolumeButtons(!volumeButtons);
  }, [volumeButtons]);

  const onToggleVolume = useCallback(() => {
    const toggle = !player.muted();
    player.muted(toggle);
    setIsMuted(toggle);

    if (!toggle) {
      handleVolume();
    } else {
      handleVolume(0);
    }

    if (isMobile) {
      toggleVolumeButtons();
    }
  }, [player, handleVolume, toggleVolumeButtons]);

  const onSwipe = useCallback(() => {
    if (!(props.fullscreen && !isDesktop)) {
      return;
    }

    if (!CookieStorageService.get('swipe-animation-shown')) {
      CookieStorageService.set('swipe-animation-shown', 'shown', { path: '/' });
    }
  }, [props.fullscreen, isDesktop]);

  const onDoubleClick = useCallback((event) => {
    if (!sfw) {
      handleVideoDoubleClick?.(event);
    }
  }, [handleVideoDoubleClick, sfw]);

  useEffect(() => {
    if (!(props.fullscreen && !isDesktop)) {
      return;
    }

    const videoWrapper = document.getElementById('vjs_video_3');
    if (videoWrapper) {
      const scrollableMidle = videoWrapper.scrollWidth / 2;
      const videoMiddle = videoWrapper.offsetWidth / 2;
      videoWrapper.scrollLeft = scrollableMidle - videoMiddle;
    }
  }, [props.fullscreen, isDesktop]);

  const playerMuted = player?.muted();

  useEffect(() => {
    if (playerMuted !== isMuted) {
      setIsMuted(playerMuted);
    }
  }, [playerMuted, isMuted]);

  const onScrollToPlayer = useCallback(() => {
    document.documentElement.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  const onEnter = useCallback(() => {
    onHover?.();
    temporaryPlayerService.toggleControls(true);
    setShowUnmuteButton(true);
  }, [onHover, temporaryPlayerService]);

  const onLeave = useCallback(() => {
    offHover?.();
    temporaryPlayerService.toggleControls(false);
    setShowUnmuteButton(false);
  }, [offHover, temporaryPlayerService]);

  const containerClassName = [
    styles.jinkPlayerContainer,
    {
      [styles.pip]: showPip && !pipClosed,
      [styles.pipAds]: showPip && !pipClosed && adsIsPlaying,
      [styles.pipSfw]: showPip && !pipClosed && sfw,
      [styles.mobileFullscreen]: !isDesktop && props.fullscreen,
      [styles.portrait]: !isDesktop && isFullScreen,
      [styles.customFullscreen]: isCustomFullscreenActive,
    },
  ];
  const videoStyle = {};
  if (isFullScreen) {
    videoStyle.style = {};
    const height = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    const fullScreenMargin = 80;
    videoStyle.style.height = height + fullScreenMargin;
  }

  const isPipClosed = !(showPip && !pipClosed);

  useEffect(() => {
    let buttonsHideTimeout = undefined;

    if (!isDesktop) {
      setHideVolume(false);
      buttonsHideTimeout = setTimeout(() => {
        setHideVolume(true);
      }, volumeHideDelay);
    }

    return () => clearTimeout(buttonsHideTimeout);
  }, [volumeButtons, isDesktop]);

  const togglePortraitTrueFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const handleCloseFullscreen = useCallback(() => {
    if (!isInFullscreen()) {
      closeCustomFullscreen();
    }
  }, [isInFullscreen, closeCustomFullscreen]);

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleCloseFullscreen);
    document.addEventListener('webkitfullscreenchange', handleCloseFullscreen);
    document.addEventListener('mozfullscreenchange', handleCloseFullscreen);
    document.addEventListener('msfullscreenchange', handleCloseFullscreen);

    return () => {
      document.removeEventListener('fullscreenchange', handleCloseFullscreen);
      document.removeEventListener('webkitfullscreenchange', handleCloseFullscreen);
      document.removeEventListener('mozfullscreenchange', handleCloseFullscreen);
      document.removeEventListener('msfullscreenchange', handleCloseFullscreen);
    };
  }, [handleCloseFullscreen]);

  const location = useLocation();
  const history = useHistory();

  const switchEventFeed = useCallback(() => {
    track({
      eventname: 'realityShowPIPFeedSwitch',
      details: { realityShow: realityShow || {} },
      room: profileInfo.username,
      username,
    });

    const queryParams = new URLSearchParams(location.search);
    const streamName = queryParams.get('cam') || 'naked';

    if (streamName === 'naked') {
      queryParams.set('cam', 'uncut');
      history.push({ search: queryParams.toString() });

      setMainEventStream(false);
    } else {
      queryParams.set('cam', 'naked');
      history.push({ search: queryParams.toString() });

      setMainEventStream(true);
    }
  }, [
    history,
    location,
    profileInfo.username,
    realityShow,
    setMainEventStream,
    username,
  ]);

  const showStreamQualityControl = !isDesktop && showQualityOptions;
  const showToggleMuteUnmuteButton = !isDesktop && broadcastStatus !== BROADCAST_STATUS.SPYING;
  const showTogglePortraitFullScreen = !deviceInLandscape && !isDesktop;

  const hasSecondStream = Boolean(mainEventSecondaryStream);
  const isMainEvent = Boolean(realityShow?.mainEvent);
  const showSwitchEventStreams = !isDesktop && isMainEvent && hasSecondStream;
  const { isChatShown } = ChatStore;
  const { broadcasterInsideGroupShow } = GroupShowStore;
  const { embedProfileV2 } = props;

  const buttonsContainerClasses = classNames({
    [styles.buttonsContainer]: true,
    [styles.buttonsContainerTheatre]: (!isChatShown && !broadcasterInsideGroupShow) || embedProfileV2,
    [styles.hidden]: hideVolume,
    [styles.buttonsContainerFullScreen]: ProfileStore.portraitTrueFullScreen && !deviceInLandscape,
  });

  const unmuteVideoContainerClassNames = classNames({
    [styles.unmuteVideoWrapperDesktop]: true,
    [styles.unmuteWrapperHidden]: !showUnmuteButton,
  });

  const videoVolume = adsIsPlaying ? 0 : volume;

  const volumeIconElement = useMemo(() => {
    if (broadcastStatus === BROADCAST_STATUS.SPYING || sfw) {
      return null;
    }

    const VolumeIcon = isMuted ? VolumeMute : VolumeUp;

    return (
      <VolumeIcon
        className={styles.volumePip}
        onClick={onToggleVolume}
      />
    );
  }, [
    broadcastStatus,
    isMuted,
    sfw,
    onToggleVolume,
  ]);

  return (
    <div
      ref={container}
      className={classNames(containerClassName)}
      style={{ height: videoStyle.style?.height }}
      onDoubleClick={onDoubleClick}
      onMouseLeave={onLeave}
      onMouseOver={onEnter}
      onTouchMove={onSwipe}
      onTouchStart={toggleVolumeButtons}
    >
      {buffering && (
        <div className={styles.spinnerOverlay} id="bufferingSpinner">
          <CircularProgress className={styles.spinnerLoading} />
        </div>
      )}

      {props.fullscreen && !isDesktop && !CookieStorageService.get('swipe-animation-shown') && (
        <TouchAppIcon className={styles.touchIcon} />
      )}

      <div className={buttonsContainerClasses}>
        {showToggleMuteUnmuteButton && (
          <div className={styles.volumeButton} onClick={onToggleVolume}>
            {volume === 0 || isMuted ? <VolumeOffIcon /> : <VolumeUp />}
          </div>
        )}
        {showStreamQualityControl && (
          <StreamQualityOptions
            activeOption={activeStreamQualityOption}
            isDesktop={isDesktop}
            isInTheatre={!ChatStore.isChatShown}
            options={streamQualityOptions}
            onStreamQualityChange={handleStreamQualityChange}
          />
        )}
        {showTogglePortraitFullScreen && (
          <div className={styles.fullScreenNormalRoomState} onClick={togglePortraitTrueFullScreen}>
            {!isFullScreen ? <FullscreenIcon /> : <FullscreenExitIcon />}
          </div>
        )}
        {showSwitchEventStreams && (
          <div className={styles.switchEventFeed} onClick={switchEventFeed}>
            <SwitchVideoIcon />
          </div>
        )}
      </div>

      {adsIsPlaying ? (
        <>
          <JinkPlayerAdsContainer {...props} />
          {isPipClosed && !isMobile && renderViewerControls()}
          <button className={styles.upgradeToGoldButton} onClick={props.upgradeToGold}>
            {translate('v2profile.video.ads.upgrade.to.gold')}
          </button>
        </>
      ) : null }

      <Background videoNode={video.current?.$videoEl} />
      <JinkPlayerTransformContainer forwardedRef={video} {...props} volume={videoVolume} />
      {isPipClosed && !isMobile && renderViewerControls()}
      {isPipClosed && (
        <>
          <DMCABadge isDesktop={props.isDesktop} isTrueFullScreen={isFullScreen} />
          {showUnmute && (
            <button className={unmuteVideoContainerClassNames} onClick={onUnmuteVideo}>
              <span
                className={styles.unmuteVideoBtnDesktop}
              >
                {translate('v2profile.video.unmute.video.text')}</span>
            </button>
          )}
        </>
      )}

      {showPip && !pipClosed && !adsIsPlaying && (
        <>
          <button className={styles.scrollButton} onClick={onScrollToPlayer} />
          <CloseIcon className={styles.closePip} onClick={onClosePip} />
          {volumeIconElement}
        </>
      )}
    </div>
  );
};

JinkPlayerContainer.propTypes = {
  adsIsPlaying: PropTypes.bool.isRequired,
  buffering: PropTypes.bool.isRequired,
  fullscreen: PropTypes.bool.isRequired,
  handleVideoDoubleClick: PropTypes.func.isRequired,
  handleVolume: PropTypes.func.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  renderViewerControls: PropTypes.func.isRequired,
  temporaryPlayerService: PropTypes.object.isRequired,
  upgradeToGold: PropTypes.func.isRequired,
  videoLoaded: PropTypes.bool.isRequired,
  closeCustomFullscreen: PropTypes.func,
  embedProfileV2: PropTypes.bool,
  handleStreamQualityChange: PropTypes.func,
  isCustomFullscreenActive: PropTypes.bool,
  offHover: PropTypes.func,
  player: PropTypes.object,
  volume: PropTypes.number,
  onHover: PropTypes.func,
};

JinkPlayerContainer.defaultProps = {
  embedProfileV2: false,
  offHover: () => undefined,
  onHover: () => undefined,
  player: undefined,
};

export default observer(JinkPlayerContainer);
